/* jshint esversion: 6, -W097, -W040 */
/* globals module */
'use strict';

/**
 * @module enervee_utils.common
 */
function StorageService() {
    var _pub = {
        helpers: {},
    };
    var _data = {};

    /**
     * Returns the value of a (potentially) nested object from the data store.
     *
     * @param {Object} obj the object to get the value from
     * @param {String|String[]} path - the path related to where the resource's
     * data is to be stored. See also: `normalizePath`.
     * @param {Object} [options] - Further options.
     * @param {*} [options.defaultValue] - The value that should be
     * returned if the value at the requested location is strictly
     * `undefined`.
     */
    _pub.helpers.get = function (obj, path, options) {
        options = options || {};
        return getOrSetNestedProperty(obj, path, {
            defaultValue: options.defaultValue,
        });
    };

    /**
     * Sets the value for a (potentially) nested value in the data store.
     *
     * @param {Object} obj the object to set the value on
     * @param {String|String[]} path - the path related to where the resource's
     * data is to be stored. See also: `normalizePath`.
     * @param {*} data - the data to set at the potentially nested
     * location.
     */
    _pub.helpers.set = function (obj, path, data) {
        return getOrSetNestedProperty(obj, path, {
            newValue: data,
        });
    };

    /**
     * Clears the value for a (potentially) nested value in the data store.
     * 
     * @param {Object} obj the object to set the value on
     * @param {String|String[]} path - the path related to where the resource's
     * data is to be stored. See also: `normalizePath`.
     */
    _pub.helpers.clear = function (obj, path) {
        return getOrSetNestedProperty(obj, path, {
            newValue: undefined,
        });
    }

    /**
     * Returns the value of a (potentially) nested object from the data store.
     * NOTE: see params for _pub.helpers.get
     */
    _pub.get = function (path, options) {
        return _pub.helpers.get(_data, path, options);
    };

    /**
     * Sets the value for a (potentially) nested value in the data store.
     * NOTE: see params for _pub.helpers.set
     */
    _pub.set = function (path, data) {
        return _pub.helpers.set(_data, path, data);
    };

    /**
     * Clears the value for a (potentially) nested value in the data store.
     * NOTE: see params for _pub.helpers.clear
     */
    _pub.clear = function (path) {
        return _pub.helpers.clear(_data, path);
    };

    /**
     * Helper function to handle getting/setting nested properties.
     * If a `newValue` is passed, sets the new value before returning.
     *
     * @param {Object} obj The main data store object
     * @param {String|String[]} path The path to the desired property. Can
     * expressed as a string of dot-separated values, or an array of
     * strings.
     * @param {Object} [options={}] See also: resolvePath options
     * @param {Boolean} [options.returnDefaultInsteadOfNull=true] If set to true, then if the
     * retrieved value is `null`, then this function will return options.defaultValue
     * instead of `null`.
     * @param {*} [options.newValue] If passed, sets the value of the nested
     * property before returning.
     * @param {*} [options.defaultValue] The value that should be
     * returned if the value at the requested location is strictly
     * `undefined`.
     */
    function getOrSetNestedProperty(obj, path, options) {
        var defaultOptions = {
            returnDefaultInsteadOfNull: true,
        };
        options = Object.assign(defaultOptions, options || {});
        path = normalizePath(path);
        var isSet = options.hasOwnProperty('newValue');
        options.makeMissingProps = isSet;
        var pathResolution = resolvePath(obj, path, options);
        // If a new value is passed, set it
        if (isSet) {
            pathResolution.parent[pathResolution.prop] = options.newValue;
            pathResolution.value = options.newValue;
        }
        /**
         * If the stored value is undefined or if the value is null and the options specify
         * returning the defaultVal instead of null, return the defaultVal (if one has been
         * passed)
         */
        if (pathResolution.value === null && options.returnDefaultInsteadOfNull) {
            return options.defaultValue || pathResolution.value;
        } else if (angular.isUndefined(pathResolution.value)) {
            return options.defaultValue;
        } else {
            return pathResolution.value;
        }
    }

    /**
     * Resolves a path into the last accessible property and the object we're attempting to
     * access it on.
     *
     * @param {Object} obj - the main data store object
     * @param {String|String[]} path - the path to the desired property. Can
     * expressed as a string of dot-separated values, or an array of
     * strings.
     * @param {Object} [options={}]
     * @param {Boolean} [options.breakOnNull=true] If true, the loop to resolve nested keys
     * will stop if any encountered values are `null` (potentially returning `null`).
     * @param {Boolean} [options.makeMissingProps=false] If true, creates empty objects for any
     * properties that are undefined in order to reach the last key in the path.
     * @return {Object} An object with the prop, parent, and value (of parent[prop])
     */
    function resolvePath(obj, path, options) {
        var defaultOptions = {
            breakOnNull: true,
            makeMissingProps: false,
        };
        options = Object.assign(defaultOptions, options || {});
        path = normalizePath(path);
        var key;
        // Drill down through the nested objects until we're out of keys or we break
        while (path.length > 0) {
            key = String(path[0]);
            var nestedObj = obj[key];
            var isLastKey = path.length === 1;
            // If the value of the key we're currently on is undefined:
            if (angular.isUndefined(nestedObj)) {
                /**
                 * If we're getting and not setting, then just break out of the while loop
                 * right here.
                 */
                if (!options.makeMissingProps) {
                    break;
                }
                /**
                 * If we are setting and this isn't the last key, we should create an empty
                 * object as this key's value so that we can reach the last key.
                 */
                if (!isLastKey) {
                    obj[key] = {};
                    nestedObj = obj[key];
                }
            }
            /**
             * If the value of the key we're currently on is null and the options specify
             * breaking on null values, then just break out of the while loop.
             */
            if (nestedObj === null && options.breakOnNull) {
                break;
            }
            /**
             * If we're not on the last key, reassign nestedObj so that we can keep
             * drilling down into the object tree and reach the last key.
             */
            if (!isLastKey) {
                obj = nestedObj;
            }
            // Remove a key from the beginning of the path to progress the loop
            path.shift();
        }
        // Return a key + object pair so that we can set the property if need be
        return {
            prop: key,
            parent: obj,
            value: obj[key],
        };
    }

    /**
     * Helper function to flatten potentially complex paths into a standard format.
     * @example
     * normalizePath('foo.bar'); // ['foo', 'bar']
     * @example
     * normalizePath(['foo', 'bar']); // ['foo', 'bar']
     * @example
     * normalizePath(['foo', 'bar.fizz']); // ['foo', 'bar', 'fizz']
     * @example
     * normalizePath(['foo', ['bar', 'fizz.buzz']]); // ['foo', 'bar', 'fizz', 'buzz']
     *
     * @param {String|Array<String|Array>} path A list of dot-separated strings and/or arrays.
     * @return {Array<String>} A flattened array of strings.
     */
    function normalizePath(path) {
        if (typeof path === 'string') {
            return path.split('.');
        } else if (!Array.isArray(path)) {
            path = [String(path)];
        }
        /**
         * If the argument was already array, we should make sure that any nested
         * arrays are already flattened to a list of strings, then flatten _this_
         * array.
         */
        return path.reduce(function (newPath, subPath) {
            return newPath.concat(normalizePath(subPath));
        }, []);
    }

    return _pub;
}

module.exports = StorageService;
